import { faCancel, faFileContract } from '@fortawesome/free-solid-svg-icons'
import { faSend } from '@fortawesome/pro-regular-svg-icons'
import React, { PropsWithChildren, useMemo } from 'react'
import { CardProps, PrimaryActionsArray } from 'src/components/Card'
import useToggleState from 'src/hooks/useToggleState'
import useToggleStateValue from 'src/hooks/useToggleStateValue'
import {
  useEeocByCandidate,
  useMutateSendEeocByCandidate,
  useMutateVoidEeocByCandidate,
  UseMutateSendEeocByCandidateReturn,
  UseMutateVoidEeocByCandidateReturn,
  useCandidateDetailsById
} from 'src/queries'

interface Actions {
  primary: NonNullable<PrimaryActionsArray>
  secondary: NonNullable<CardProps['secondaryActions']>
}

interface UseEeocContext {
  candidate: ReturnType<typeof useCandidateDetailsById>
  eeoc: ReturnType<typeof useEeocByCandidate>
  voidEeoc: UseMutateVoidEeocByCandidateReturn
  send: UseMutateSendEeocByCandidateReturn
  sendMode: boolean
  resendMode: boolean
  actions: Actions
  modals: {
    send: {
      isOpen: boolean
      toggle: () => void
    }
    void: {
      isOpen: boolean
      toggle: () => void
    }
    viewer: {
      isOpen: boolean
      toggle: () => void
      value: string | undefined
    }
  }
}

const eeocContext = React.createContext<UseEeocContext | undefined>(undefined)

interface EeocContextProviderProps extends PropsWithChildren {
  candidate: string
}

const EeocContextProvider: React.FC<EeocContextProviderProps> = (props) => {
  const candidateId = props.candidate
  const candidate = useCandidateDetailsById(candidateId)
  const eeoc = useEeocByCandidate(candidateId)
  const voidEeoc = useMutateVoidEeocByCandidate()
  const send = useMutateSendEeocByCandidate()
  const [sendModal, , toggleSend] = useToggleState(false)
  const [voidModal, , toggleVoid] = useToggleState(false)
  const viewer = useToggleStateValue<string | undefined>(undefined)

  const status = eeoc?.status
  const sendMode = status === 'voided' || eeoc === null
  const resendMode = status !== undefined &&
    status !== null &&
    ['sent', 'signed'].includes(status)

  const actions = useMemo<Actions>(() => {
    const actions: Actions = {
      primary: [],
      secondary: []
    }

    if (sendMode || resendMode) {
      actions.primary.push({
        key: 'send',
        icon: faSend,
        onClick: toggleSend
      })
    }
    if (resendMode) {
      actions.secondary.push({
        key: 'void',
        icon: faCancel,
        text: 'Void EEOC Paperwork',
        onClick: toggleVoid
      })
    }
    const doc = eeoc?.documents ?? undefined
    if (typeof doc === 'string' && status === 'signed') {
      actions.secondary.push({
        key: 'view',
        text: 'View EEOC Paperwork',
        icon: faFileContract,
        onClick: () => {
          viewer.toggle(doc)
        }
      })
    }
    return actions
  }, [sendMode, resendMode, eeoc])

  const value: UseEeocContext = {
    eeoc,
    candidate,
    voidEeoc,
    send,
    sendMode,
    resendMode,
    actions,
    modals: {
      send: {
        isOpen: sendModal,
        toggle: toggleSend
      },
      void: {
        isOpen: voidModal,
        toggle: toggleVoid
      },
      viewer: {
        isOpen: viewer.hasState,
        toggle: viewer.toggle,
        value: viewer.value
      }
    }
  }
  return (
    <eeocContext.Provider value={value}>
      {props.children}
    </eeocContext.Provider>
  )
}

const useEeocContext = (): UseEeocContext => {
  const context = React.useContext(eeocContext)
  if (context === undefined) {
    throw new Error('useEeocContext must be used within a EeocContextProvider')
  }
  return context
}

export { EeocContextProvider, useEeocContext, eeocContext }
